import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Checkbox, Spinner } from '@monksflow/monks-ui';
import { ArrowRight, Check, RefreshCw, Sparkles, TriangleAlert } from 'lucide-react';
import { useSuggestAbbreviations, type AbbreviationSuggestion } from '@/entities/taxonomy';
import { FormSheet } from '@/shared/ui/FormSheet';

/** One selected platform's budget, for explaining which limit binds. */
export interface AbbreviationChannel {
  platform: string;
  limit: number;
  length: number;
  over: boolean;
}

export interface AbbreviationSuggestionsModalProps {
  open: boolean;
  taxonomyId: string;
  versionId: string;
  /** The channel whose limit binds — the strictest one being exceeded. */
  platform?: string;
  /** Character budget the generated name must fit under (that channel's limit). */
  targetLength: number;
  /** Worst-case length as it stands, so the confirm step can state the gap. */
  currentLength: number;
  /** Every selected platform, so the choice of budget is shown rather than asserted. */
  channels: AbbreviationChannel[];
  /** Apply a pack of code changes in one edit. */
  onApply: (changes: { fieldId: string; value: string; to: string }[]) => void;
  /** Apply date-mask changes. Omit when the schema is read-only. */
  onApplyDateFormats?: (changes: { fieldId: string; to: string }[]) => void;
  /** Unlink bound fields and take ownership of their values. Omit when read-only. */
  onApplyUnlinks?: (
    changes: { fieldId: string; values: { value: string; code: string }[] }[],
  ) => void;
  onClose: () => void;
}

const keyOf = (s: { fieldId: string; value: string }) => `${s.fieldId}::${s.value}`;

/**
 * LLM-assisted abbreviation review, in two steps.
 *
 * It CONFIRMS first: shortening is a bulk edit to shared codes, and the budget it
 * targets is a decision (the strictest platform, so one pass satisfies every
 * channel) that the user should see before it happens rather than infer from the
 * results. The old version fired on open, so the only way to learn what it was
 * aiming at was to read the suggestions.
 *
 * Then it reports whether the pack actually REACHES that budget. Suggestions
 * that leave the name over the limit used to look identical to ones that fixed
 * it.
 */
export function AbbreviationSuggestionsModal({
  open,
  taxonomyId,
  versionId,
  platform,
  targetLength,
  currentLength,
  channels,
  onApply,
  onApplyDateFormats,
  onApplyUnlinks,
  onClose,
}: AbbreviationSuggestionsModalProps) {
  const { t } = useTranslation('knowledge');
  const suggest = useSuggestAbbreviations(taxonomyId);
  const { mutate, data, isPending, isError, reset } = suggest;
  const [applied, setApplied] = useState<Set<string>>(new Set());
  const [formatsApplied, setFormatsApplied] = useState(false);
  const [unlinksApplied, setUnlinksApplied] = useState(false);
  const [started, setStarted] = useState(false);
  const [includeSharedLists, setIncludeSharedLists] = useState(false);

  // Reset to the confirm step every open — a fresh open is a fresh decision.
  useEffect(() => {
    if (open) {
      setApplied(new Set());
      setFormatsApplied(false);
      setUnlinksApplied(false);
      setStarted(false);
      setIncludeSharedLists(false);
    } else {
      reset();
    }
    // `reset` is a stable react-query ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Memoized so the `groups` memo below isn't invalidated by a fresh [] every render.
  const suggestions = useMemo(() => data?.suggestions ?? [], [data]);
  const mustSave = Math.max(0, currentLength - targetLength);

  // Group suggestions by field for a scannable review.
  const groups = useMemo(() => {
    const byField = new Map<
      string,
      { fieldId: string; fieldName: string; items: AbbreviationSuggestion[] }
    >();
    for (const s of suggestions) {
      if (!byField.has(s.fieldId))
        byField.set(s.fieldId, { fieldId: s.fieldId, fieldName: s.fieldName, items: [] });
      byField.get(s.fieldId)!.items.push(s);
    }
    return [...byField.values()];
  }, [suggestions]);

  const pending = suggestions.filter((s) => !applied.has(keyOf(s)));

  const apply = (items: AbbreviationSuggestion[]) => {
    if (items.length === 0) return;
    onApply(items.map((s) => ({ fieldId: s.fieldId, value: s.value, to: s.to })));
    setApplied((prev) => {
      const next = new Set(prev);
      items.forEach((s) => next.add(keyOf(s)));
      return next;
    });
  };

  const start = (withSharedLists = includeSharedLists) => {
    setIncludeSharedLists(withSharedLists);
    setStarted(true);
    // A new plan has nothing applied yet. These used to reset only on open, so
    // re-planning from inside the results (the two buttons below) left every
    // apply button disabled on "Applied" and the only way out was closing the
    // dialog.
    setApplied(new Set());
    setFormatsApplied(false);
    setUnlinksApplied(false);
    mutate({ versionId, platform, targetLength, includeSharedLists: withSharedLists });
  };

  const formatChanges = data?.formatChanges ?? [];
  const applyFormats = () => {
    if (!onApplyDateFormats || formatChanges.length === 0) return;
    onApplyDateFormats(formatChanges.map((c) => ({ fieldId: c.fieldId, to: c.to })));
    setFormatsApplied(true);
  };

  const unlinkChanges = data?.unlinkChanges ?? [];
  const applyUnlinks = () => {
    if (!onApplyUnlinks || unlinkChanges.length === 0) return;
    onApplyUnlinks(unlinkChanges.map((c) => ({ fieldId: c.fieldId, values: c.values })));
    setUnlinksApplied(true);
  };

  // ── Step 1: confirm the budget ────────────────────────────────────────────
  if (!started) {
    return (
      <FormSheet
        className="w-[34rem]"
        open={open}
        onOpenChange={(next) => !next && onClose()}
        title={t('taxonomy.abbreviationSuggestionsModal.shortenAi')}
      >
        <FormSheet.Body className="flex min-h-0 flex-col gap-4 overflow-y-auto">
          <p className="text-sm text-muted-foreground">
            {t('taxonomy.abbreviationSuggestionsModal.name')}{' '}
            <span className="font-mono font-semibold text-foreground">{currentLength}</span>{' '}
            {t('taxonomy.abbreviationSuggestionsModal.charactersWorstCase')}
            {platform ? (
              <>
                {' '}
                <span className="font-medium text-foreground">{platform}</span>{' '}
                {t('taxonomy.abbreviationSuggestionsModal.tightestLimitYouAposVe')}{' '}
                <span className="font-mono font-semibold text-foreground">{targetLength}</span>{' '}
                {t('taxonomy.abbreviationSuggestionsModal.makesFit')}{' '}
                <span className="font-medium text-foreground">
                  {t('taxonomy.abbreviationSuggestionsModal.every')}
                </span>{' '}
                {t('taxonomy.abbreviationSuggestionsModal.platformOnce')}
              </>
            ) : (
              <>
                {' '}
                {t('taxonomy.abbreviationSuggestionsModal.budget')}{' '}
                <span className="font-mono font-semibold text-foreground">{targetLength}</span>{' '}
                {t('taxonomy.abbreviationSuggestionsModal.characters')}
              </>
            )}
          </p>

          {channels.length > 0 && (
            <ul className="flex flex-col divide-y divide-border-light rounded-lg border border-border-light">
              {channels.map((c) => (
                <li
                  key={c.platform}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
                >
                  <span className="font-medium text-foreground">{c.platform}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-muted-foreground">
                      {c.length}/{c.limit}
                    </span>
                    {c.over ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 font-medium text-destructive">
                        <TriangleAlert className="size-3" aria-hidden />
                        {c.length - c.limit} {t('taxonomy.abbreviationSuggestionsModal.over')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                        <Check className="size-3" aria-hidden />
                        {t('taxonomy.abbreviationSuggestionsModal.fits')}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <p className="rounded-lg border border-border-light bg-muted/40 p-3 text-xs text-muted-foreground">
            <Trans
              i18nKey="taxonomy.abbreviationSuggestionsModal.planReduction"
              ns="knowledge"
              count={mustSave}
              components={{
                amount: <span className="font-mono font-semibold text-foreground" />,
              }}
            />
          </p>

          {/* Codes owned by a shared List reach every schema bound to that list,
              so this is an explicit choice rather than a silent default. */}
          <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-border-light p-3">
            <Checkbox
              checked={includeSharedLists}
              onCheckedChange={(next) => setIncludeSharedLists(next === true)}
              className="mt-0.5"
              aria-label={t('taxonomy.abbreviationSuggestionsModal.alsoShortenCodesOwnedShared')}
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-foreground">
                {t('taxonomy.abbreviationSuggestionsModal.allowUnlinkingFieldsFollowShared')}
              </span>
              <span className="text-xs text-muted-foreground">
                {t('taxonomy.abbreviationSuggestionsModal.mostFieldsHereTakeTheir')}
              </span>
            </span>
          </label>
        </FormSheet.Body>

        <FormSheet.Footer
          primary={{
            label: t('taxonomy.abbreviationSuggestionsModal.shortenToChars', {
              limit: targetLength,
            }),
            onClick: () => start(),
          }}
          secondary={{ label: t('taxonomy.abbreviationSuggestionsModal.cancel'), onClick: onClose }}
        />
      </FormSheet>
    );
  }

  // ── Step 2: review the plan ───────────────────────────────────────────────
  const projected = data?.estimatedLength;
  const fits = data?.fitsTarget ?? false;

  return (
    <FormSheet
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={t('taxonomy.abbreviationSuggestionsModal.suggestedAbbreviations')}
      className="flex w-[34rem] flex-col overflow-hidden sm:max-w-[40rem]"
    >
      <FormSheet.Body className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Sparkles className="size-3.5" aria-hidden />
          {platform
            ? t('taxonomy.abbreviationSuggestionsModal.goalWithPlatform', {
                platform,
                limit: targetLength,
              })
            : t('taxonomy.abbreviationSuggestionsModal.goal', { limit: targetLength })}
        </p>

        {isPending && (
          <div className="flex flex-col items-center gap-2 py-12 text-sm text-muted-foreground">
            <Spinner />
            {t('taxonomy.abbreviationSuggestionsModal.planning')} {mustSave}
            {t('taxonomy.abbreviationSuggestionsModal.characterReduction')}
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <TriangleAlert className="size-6 text-destructive" aria-hidden />
            <p className="text-sm text-muted-foreground">
              {t('taxonomy.abbreviationSuggestionsModal.couldnAposTGenerateSuggestions')}
            </p>
            {/* Arrow, not a bare `start`: the click event would arrive as
                `withSharedLists` and silently widen the plan's scope. */}
            <Button variant="outline" size="mini" onClick={() => start()}>
              <RefreshCw className="size-3.5" />
              {t('taxonomy.abbreviationSuggestionsModal.tryAgain')}
            </Button>
          </div>
        )}

        {/* The outcome, stated plainly: a pack that gets closer without reaching
            the budget used to be indistinguishable from one that fixed it. */}
        {!isPending &&
          !isError &&
          projected !== undefined &&
          (suggestions.length > 0 || formatChanges.length > 0 || unlinkChanges.length > 0) && (
            <div
              className={`flex items-start gap-2 rounded-lg border p-3 text-xs ${
                fits
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
                  : 'border-destructive/30 bg-destructive/10 text-destructive'
              }`}
            >
              {fits ? (
                <Check className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              ) : (
                <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              )}
              {/* `data.currentLength` rather than the prop: the server measures a
                list-bound field against its LIST, which is what the name will
                actually be built from. */}
              <span>
                {fits ? (
                  <>
                    {t('taxonomy.abbreviationSuggestionsModal.applyingWholePlanTakesName')}{' '}
                    <span className="font-mono font-semibold">
                      {data?.currentLength ?? currentLength}
                    </span>{' '}
                    {t('taxonomy.abbreviationSuggestionsModal.to')}{' '}
                    <span className="font-mono font-semibold">{projected}</span>{' '}
                    {t('taxonomy.abbreviationSuggestionsModal.charactersWithin')} {targetLength}{' '}
                    {t('taxonomy.abbreviationSuggestionsModal.budget2')}
                  </>
                ) : (
                  <>
                    {t('taxonomy.abbreviationSuggestionsModal.planGetsName')}{' '}
                    <span className="font-mono font-semibold">{projected}</span>{' '}
                    {t('taxonomy.abbreviationSuggestionsModal.charactersStill')}{' '}
                    <span className="font-mono font-semibold">{data?.shortfall ?? 0}</span>{' '}
                    {t('taxonomy.abbreviationSuggestionsModal.over2')} {targetLength}{' '}
                    {t('taxonomy.abbreviationSuggestionsModal.budgetEveryRemainingCodeAlready')}
                  </>
                )}
              </span>
            </div>
          )}

        {/* Offer the trade rather than just reporting a shortfall: the chars are
            there, they just live in a shared vocabulary. */}
        {!isPending && !isError && !fits && (data?.sharedListPotential ?? 0) > 0 && (
          <div className="flex flex-col items-start gap-2 rounded-lg border border-border-light bg-muted/40 p-3 text-xs text-muted-foreground">
            <span>
              {t('taxonomy.abbreviationSuggestionsModal.fieldsFollowSharedListCould')}{' '}
              <span className="font-mono font-semibold text-foreground">
                {data?.sharedListPotential}
              </span>{' '}
              {t('taxonomy.abbreviationSuggestionsModal.charactersWhichWouldCloseGap')}
            </span>
            <Button variant="outline" size="mini" onClick={() => start(true)}>
              <RefreshCw className="size-3.5" />
              {t('taxonomy.abbreviationSuggestionsModal.rePlanUnlinkingThoseFields')}
            </Button>
          </div>
        )}

        {/* Unlinks: shortening a bound field without editing the shared list. */}
        {!isPending && unlinkChanges.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('taxonomy.abbreviationSuggestionsModal.fieldsUnlink')}
            </span>
            <p className="text-xs text-muted-foreground">
              {t('taxonomy.abbreviationSuggestionsModal.theseTakeTheirOwnCopy')}
            </p>
            <ul className="flex flex-col divide-y divide-border-light rounded-lg border border-border-light">
              {unlinkChanges.map((c) => (
                <li key={c.fieldId} className="flex items-center gap-3 px-3 py-2 text-xs">
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-foreground">{c.fieldName}</span>
                    <span className="truncate text-muted-foreground">
                      {t('taxonomy.abbreviationSuggestionsModal.wasFollowing')} {c.listName}
                      {c.schemaCount > 1
                        ? t('taxonomy.abbreviationSuggestionsModal.usedBySchemas', {
                            count: c.schemaCount,
                          })
                        : ''}
                    </span>
                  </span>
                  <span className="flex items-center gap-1 font-mono">
                    <span className="text-muted-foreground line-through">{c.fromMaxLength}</span>
                    <ArrowRight className="size-3 text-muted-foreground" aria-hidden />
                    <span className="rounded bg-emerald-50 px-1 py-0.5 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                      {c.toMaxLength}
                    </span>
                  </span>
                  <span className="hidden text-muted-foreground sm:block">
                    {c.values.length} {t('taxonomy.abbreviationSuggestionsModal.values')}
                  </span>
                </li>
              ))}
            </ul>
            {onApplyUnlinks && (
              <Button
                variant="outline"
                size="mini"
                className="self-start"
                disabled={unlinksApplied}
                onClick={applyUnlinks}
              >
                {unlinksApplied
                  ? t('taxonomy.abbreviationSuggestionsModal.applied')
                  : t('taxonomy.abbreviationSuggestionsModal.unlinkFields', {
                      count: unlinkChanges.length,
                    })}
              </Button>
            )}
          </div>
        )}

        {/* Date masks: the largest single wins, and previously untouchable. */}
        {!isPending && formatChanges.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('taxonomy.abbreviationSuggestionsModal.dateFormats')}
            </span>
            <ul className="flex flex-col divide-y divide-border-light rounded-lg border border-border-light">
              {formatChanges.map((c) => (
                <li key={c.fieldId} className="flex items-center gap-3 px-3 py-2 text-xs">
                  <span className="min-w-0 flex-1 truncate text-foreground">{c.fieldName}</span>
                  <span className="flex items-center gap-1 font-mono">
                    <span className="text-muted-foreground line-through">{c.from}</span>
                    <ArrowRight className="size-3 text-muted-foreground" aria-hidden />
                    <span className="rounded bg-emerald-50 px-1 py-0.5 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                      {c.to}
                    </span>
                  </span>
                  <span className="text-muted-foreground">−{c.saved}</span>
                  {c.lossy && (
                    <span
                      className="text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-400"
                      title={t('taxonomy.abbreviationSuggestionsModal.dropsDateComponentNotJust')}
                    >
                      {t('taxonomy.abbreviationSuggestionsModal.lessPrecise')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            {onApplyDateFormats && (
              <Button
                variant="outline"
                size="mini"
                className="self-start"
                disabled={formatsApplied}
                onClick={applyFormats}
              >
                {formatsApplied
                  ? t('taxonomy.abbreviationSuggestionsModal.applied')
                  : t('taxonomy.abbreviationSuggestionsModal.applyDateFormats', {
                      count: formatChanges.length,
                    })}
              </Button>
            )}
          </div>
        )}

        {!isPending &&
          !isError &&
          suggestions.length === 0 &&
          formatChanges.length === 0 &&
          unlinkChanges.length === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {t('taxonomy.abbreviationSuggestionsModal.noFurtherShorteningAvailableCodes')}
            </div>
          )}

        {!isPending && groups.length > 0 && (
          <div className="flex flex-col gap-4">
            {groups.map((g) => (
              // Keyed by id, not name: two columns may share a name (only a
              // validate WARNING), and duplicate keys remount the wrong group.
              <div key={g.fieldId} className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {g.fieldName}
                </span>
                <ul className="flex flex-col divide-y divide-border-light rounded-lg border border-border-light">
                  {g.items.map((s) => {
                    const isApplied = applied.has(keyOf(s));
                    return (
                      <li key={keyOf(s)} className="flex items-start gap-3 px-3 py-2 text-xs">
                        <span className="w-28 shrink-0 truncate text-foreground" title={s.value}>
                          {s.value}
                        </span>
                        {/* from, arrow and to sit on one line and only wrap when they cannot
                            fit; `break-all` lets a single long code wrap mid-string instead of
                            pushing the badge or Apply off the row. */}
                        <div className="flex min-w-0 flex-1 flex-col gap-1">
                          <span className="flex min-w-0 flex-wrap items-center gap-1 font-mono">
                            <span className="break-all text-muted-foreground line-through">
                              {s.from}
                            </span>
                            <ArrowRight
                              className="size-3 shrink-0 text-muted-foreground"
                              aria-hidden
                            />
                            <span className="break-all rounded bg-emerald-50 px-1 py-0.5 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                              {s.to}
                            </span>
                          </span>
                          {s.reason && <span className="text-muted-foreground">{s.reason}</span>}
                        </div>
                        <Button
                          className="shrink-0"
                          variant="outline"
                          size="mini"
                          disabled={isApplied}
                          onClick={() => apply([s])}
                        >
                          {isApplied
                            ? t('taxonomy.abbreviationSuggestionsModal.applied')
                            : t('taxonomy.abbreviationSuggestionsModal.apply')}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </FormSheet.Body>

      <FormSheet.Footer
        primary={{
          label:
            pending.length > 0
              ? t('taxonomy.abbreviationSuggestionsModal.applyAll', { count: pending.length })
              : t('taxonomy.abbreviationSuggestionsModal.done'),
          onClick: () => (pending.length > 0 ? apply(pending) : onClose()),
          disabled: isPending,
        }}
        secondary={{ label: t('taxonomy.abbreviationSuggestionsModal.close'), onClick: onClose }}
      />
    </FormSheet>
  );
}

AbbreviationSuggestionsModal.displayName = 'AbbreviationSuggestionsModal';
